import { useAuth } from '../contexts/AuthContext';

const OFFER_TYPES = {
    START_TRAINING: 'start_training',
    TRIAL: 'trial',
    WINBACK: 'winback',
    PAYMENT_RECOVERY: 'payment_recovery',
    REACTIVATE: 'reactivate',
    SPEED_TEST: 'speed_test',
    BLUEPRINT: 'blueprint',
    REGISTER: 'register',
    NO_CTA: 'no_cta',
};

const OFFER_LABELS = {
    [OFFER_TYPES.START_TRAINING]: 'Start Training',
    [OFFER_TYPES.TRIAL]: 'Start Your $1 Trial',
    [OFFER_TYPES.WINBACK]: 'Come Back to ReaderFLEX',
    [OFFER_TYPES.PAYMENT_RECOVERY]: 'Update Your Payment Method',
    [OFFER_TYPES.REACTIVATE]: 'Reactivate Your Membership',
    [OFFER_TYPES.SPEED_TEST]: 'Take the Free Speed Test',
    [OFFER_TYPES.BLUEPRINT]: 'Get My Reading Blueprint',
    [OFFER_TYPES.REGISTER]: 'Create a Free Account',
    [OFFER_TYPES.NO_CTA]: null,
};

const OFFER_SUBTEXTS = {
    [OFFER_TYPES.START_TRAINING]: 'Keep your streak going and set new personal bests.',
    [OFFER_TYPES.TRIAL]: '3 days of full access for $1, then $9/month.',
    [OFFER_TYPES.WINBACK]: 'Your membership has lapsed. Pick up where you left off.',
    [OFFER_TYPES.PAYMENT_RECOVERY]: 'Your last payment failed. Update your card to restore access.',
    [OFFER_TYPES.REACTIVATE]: 'Your subscription was canceled but still active until your period ends.',
    [OFFER_TYPES.SPEED_TEST]: 'See your reading speed and comprehension in 2 minutes.',
    [OFFER_TYPES.BLUEPRINT]: 'Get a personalized reading improvement plan.',
    [OFFER_TYPES.REGISTER]: 'Save your results and track your progress over time.',
    [OFFER_TYPES.NO_CTA]: null,
};

const OFFER_ROUTES = {
    [OFFER_TYPES.START_TRAINING]: '/dashboard',
    [OFFER_TYPES.TRIAL]: '/subscription?planType=basic&billingCycle=trial',
    [OFFER_TYPES.WINBACK]: '/subscription',
    [OFFER_TYPES.PAYMENT_RECOVERY]: '/subscription?action=update-payment',
    [OFFER_TYPES.REACTIVATE]: '/subscription',
    [OFFER_TYPES.SPEED_TEST]: '/speed-test',
    [OFFER_TYPES.BLUEPRINT]: '/speed-test',
    [OFFER_TYPES.REGISTER]: '/register',
    [OFFER_TYPES.NO_CTA]: null,
};

function hasActiveAccess(user) {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (user.hasActiveSubscription) return true;
    const sub = user.subscription;
    if (!sub) return false;
    if (sub.status === 'active' || sub.status === 'trialing') {
        if (sub.endDate && new Date(sub.endDate).getTime() <= Date.now()) return false;
        return true;
    }
    return false;
}

function hasHadTrial(user) {
    const history = user?.subscription?.paymentHistory;
    if (!history || history.length === 0) return false;
    return history.some(p => p.amount <= 1.00 && p.status === 'completed');
}

function hasHadPaidSubscription(user) {
    const history = user?.subscription?.paymentHistory;
    if (!history || history.length === 0) return false;
    return history.some(p => p.amount > 1.00 && p.status === 'completed');
}

function determineLifecycleState(user) {
    if (!user) return 'guest';
    if (user.role === 'admin') return 'admin';

    const sub = user.subscription || {};
    const status = sub.status || 'free';

    if (hasActiveAccess(user)) {
        if (status === 'trialing') return 'active_trial';
        return 'active_paid';
    }

    if (status === 'canceled') {
        if (sub.endDate && new Date(sub.endDate).getTime() > Date.now()) {
            return 'canceled_with_access';
        }
        return 'canceled_expired';
    }

    if (status === 'unpaid') return 'past_due';

    if (status === 'free') {
        if (hasHadTrial(user) && hasHadPaidSubscription(user)) return 'former_paid';
        if (hasHadTrial(user)) return 'expired_trial';
        return 'free_never_trial';
    }

    return 'free_never_trial';
}

function getOfferForState(lifecycleState, context = {}) {
    switch (lifecycleState) {
        case 'admin':
        case 'active_paid':
        case 'active_trial':
            return {
                type: OFFER_TYPES.START_TRAINING,
                label: OFFER_LABELS[OFFER_TYPES.START_TRAINING],
                subtext: OFFER_SUBTEXTS[OFFER_TYPES.START_TRAINING],
                route: OFFER_ROUTES[OFFER_TYPES.START_TRAINING],
            };

        case 'canceled_with_access':
            return {
                type: OFFER_TYPES.REACTIVATE,
                label: OFFER_LABELS[OFFER_TYPES.REACTIVATE],
                subtext: `Your access continues until ${new Date(context.endDate).toLocaleDateString()}. Reactivate to keep it going.`,
                route: OFFER_ROUTES[OFFER_TYPES.REACTIVATE],
            };

        case 'canceled_expired':
            return {
                type: OFFER_TYPES.WINBACK,
                label: OFFER_LABELS[OFFER_TYPES.WINBACK],
                subtext: OFFER_SUBTEXTS[OFFER_TYPES.WINBACK],
                route: OFFER_ROUTES[OFFER_TYPES.WINBACK],
            };

        case 'past_due':
            return {
                type: OFFER_TYPES.PAYMENT_RECOVERY,
                label: OFFER_LABELS[OFFER_TYPES.PAYMENT_RECOVERY],
                subtext: OFFER_SUBTEXTS[OFFER_TYPES.PAYMENT_RECOVERY],
                route: OFFER_ROUTES[OFFER_TYPES.PAYMENT_RECOVERY],
            };

        case 'former_paid':
            return {
                type: OFFER_TYPES.WINBACK,
                label: 'Come Back to ReaderFLEX',
                subtext: 'Reactivate your membership and pick up where you left off.',
                route: OFFER_ROUTES[OFFER_TYPES.WINBACK],
            };

        case 'expired_trial':
            return {
                type: OFFER_TYPES.WINBACK,
                label: 'Continue Your ReaderFLEX Journey',
                subtext: 'Your trial ended. Subscribe now to keep training.',
                route: OFFER_ROUTES[OFFER_TYPES.WINBACK],
            };

        case 'free_never_trial':
            return {
                type: OFFER_TYPES.TRIAL,
                label: OFFER_LABELS[OFFER_TYPES.TRIAL],
                subtext: OFFER_SUBTEXTS[OFFER_TYPES.TRIAL],
                route: OFFER_ROUTES[OFFER_TYPES.TRIAL],
            };

        case 'guest':
            return {
                type: OFFER_TYPES.SPEED_TEST,
                label: OFFER_LABELS[OFFER_TYPES.SPEED_TEST],
                subtext: OFFER_SUBTEXTS[OFFER_TYPES.SPEED_TEST],
                route: OFFER_ROUTES[OFFER_TYPES.SPEED_TEST],
            };

        default:
            return {
                type: OFFER_TYPES.SPEED_TEST,
                label: OFFER_LABELS[OFFER_TYPES.SPEED_TEST],
                subtext: OFFER_SUBTEXTS[OFFER_TYPES.SPEED_TEST],
                route: OFFER_ROUTES[OFFER_TYPES.SPEED_TEST],
            };
    }
}

export function useOfferDecision(context = {}) {
    const { user, isAuthenticated } = useAuth();

    const lifecycleState = isAuthenticated && user
        ? determineLifecycleState(user)
        : 'guest';

    const offer = getOfferForState(lifecycleState, {
        ...context,
        endDate: user?.subscription?.endDate,
    });

    return { lifecycleState, offer, hasAccess: hasActiveAccess(user) };
}

export { OFFER_TYPES, OFFER_LABELS, OFFER_ROUTES, hasActiveAccess };
